import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { ADD_NEW_FEE_PROVIDER } from 'app/pages/loanApplication/tabs/loanClosingCDS/shared/ClosingCompany/ClosingCompany.constants';
import FeeProviderForm from 'app/shared/modules/FeeProviderForm';
import FeeProviderLocationTable, {
  LocationTableWrapper,
} from 'app/shared/modules/FeeProviderLocationTable';
import _ from 'lodash';

import { closeDialogAction, openDialogAction } from 'store/global/actions';
import { getAttendantCompaniesAction } from 'store/loanApplications/actions';

import { COMPANY_CONFIGS } from '../../Content.config';
import { getClearFields } from '../../Content.utils';

import { locationDialogSx } from './constants';

export const useClosingCompanyHandlers = ({
  titleCompany,
  escrowCompany,
  sellerAttorney,
  buyerAttorney,
  attendantCompanyOptions,
  categoriesIds,
  isSaving,
  setIsLoading,
  setFormRef,
  setIsTouchedRef,
  formDataRef,
}) => {
  const dispatch = useDispatch();

  const [hasTitleCompany, setHasTitleCompany] = useState(() => !!titleCompany?.attendantCompanyId);
  const [hasEscrowCompany, setHasEscrowCompany] = useState(
    () => !!escrowCompany?.attendantCompanyId,
  );
  const [hasSellerAttorney, setHasSellerAttorney] = useState(
    () => !!sellerAttorney?.attendantCompanyId,
  );
  const [hasBuyerAttorney, setHasBuyerAttorney] = useState(
    () => !!buyerAttorney?.attendantCompanyId,
  );

  const hasStateMap = useMemo(
    () => ({
      title: { set: setHasTitleCompany },
      escrow: { set: setHasEscrowCompany },
      sellerAttorney: { set: setHasSellerAttorney },
      buyerAttorney: { set: setHasBuyerAttorney },
    }),
    [],
  );

  const groupedAttendantCompanyOptions = useMemo(
    () => _.groupBy(attendantCompanyOptions, 'type'),
    [attendantCompanyOptions],
  );

  const buildOptions = useCallback(
    ({ filterType, categoryType }) => [
      {
        name: 'Add New Fee Provider',
        value: ADD_NEW_FEE_PROVIDER,
        feeProviderCategoryId: categoriesIds[categoryType],
      },
      ...(groupedAttendantCompanyOptions[filterType] || []),
    ],
    [categoriesIds, groupedAttendantCompanyOptions],
  );

  const { titleCompanyOptions, escrowCompanyOptions, sellerAttorneyOptions, buyerAttorneyOptions } =
    useMemo(
      () => ({
        titleCompanyOptions: buildOptions(COMPANY_CONFIGS[0]),
        escrowCompanyOptions: buildOptions(COMPANY_CONFIGS[1]),
        sellerAttorneyOptions: buildOptions(COMPANY_CONFIGS[2]),
        buyerAttorneyOptions: buildOptions(COMPANY_CONFIGS[3]),
      }),
      [buildOptions],
    );

  const onChooseLocation = useCallback(
    (__, { location }, config) => {
      const fieldId = config.getFieldIds(config.prefix);

      dispatch(closeDialogAction());
      setIsTouchedRef.current(true);

      setFormRef.current(prev => ({
        ...prev,
        [fieldId.attendantCompanyLocationId]: location.feeProviderLocationId,
        [fieldId.licenseNumber]: location.licenseNumber || prev[fieldId.licenseNumber] || '',
        [fieldId.streetAddress]: location.streetAddress || '',
        [fieldId.suite]: location.suite || '',
        [fieldId.city]: location.city || '',
        [fieldId.state]: location.state || '',
        [fieldId.zipCode]: location.zipCode || '',
        ...(config.getExtraLocationFields?.(fieldId, location) || {}),
      }));
    },
    [dispatch, setFormRef, setIsTouchedRef],
  );

  const onOpenLocationTable = useCallback(
    (option, config) => {
      const onLocationRowCustomClick = (...props) => onChooseLocation(...props, config);

      dispatch(
        openDialogAction({
          header: 'Choose location',
          size: ModalSize.FULL,
          customDialogProps: {
            sx: locationDialogSx,
          },
          body: dialogProps => (
            <LocationTableWrapper>
              <FeeProviderLocationTable
                {...dialogProps}
                feeProviderId={option?.id || option?.value}
                isLoading={isSaving}
                setIsLoading={setIsLoading}
                onLocationRowCustomClick={onLocationRowCustomClick}
              />
            </LocationTableWrapper>
          ),
        }),
      );
    },
    [dispatch, isSaving, setIsLoading, onChooseLocation],
  );

  const onCompanyChange = useCallback(
    (e, option, config) => {
      const fieldId = config.getFieldIds(config.prefix);
      const setHasCompany = hasStateMap[config.prefix].set;

      if (!option) {
        setIsTouchedRef.current(true);
        setHasCompany(false);
        setFormRef.current(prev => ({
          ...prev,
          ...getClearFields(fieldId),
        }));

        return;
      }

      const onUpdateForm = selectedOption => {
        setIsTouchedRef.current(true);
        setFormRef.current(prev => ({
          ...prev,
          [fieldId.attendantCompanyId]: selectedOption,
          [fieldId.name]: selectedOption.name || '',
        }));

        setHasCompany(true);

        if (selectedOption.locations?.length === 1) {
          return onChooseLocation(null, { location: selectedOption.locations[0] }, config);
        }

        onOpenLocationTable(selectedOption, config);
      };

      if (option.value === ADD_NEW_FEE_PROVIDER) {
        const onSuccessCreate = payload =>
          dispatch(getAttendantCompaniesAction()).then(() => {
            dispatch(closeDialogAction());
            onUpdateForm({ ...payload, value: payload.feeProviderId });
          });

        return dispatch(
          openDialogAction({
            header: 'Add Fee Provider',
            isRestyled: true,
            size: ModalSize.LARGE,
            withDivider: true,
            hasScrollY: false,
            body: dialogProps => (
              <FeeProviderForm
                isDialog
                feeProviderCategoryId={option.feeProviderCategoryId}
                isLocationsIncluded={false}
                onSuccessCreate={onSuccessCreate}
                {...dialogProps}
              />
            ),
          }),
        );
      }

      onUpdateForm(option);
    },
    [hasStateMap, onChooseLocation, onOpenLocationTable, dispatch, setFormRef, setIsTouchedRef],
  );

  const {
    onTitleCompanyChange,
    onEscrowCompanyChange,
    onSellerAttorneyChange,
    onBuyerAttorneyChange,
  } = useMemo(
    () => ({
      onTitleCompanyChange: (e, option) => onCompanyChange(e, option, COMPANY_CONFIGS[0]),
      onEscrowCompanyChange: (e, option) => onCompanyChange(e, option, COMPANY_CONFIGS[1]),
      onSellerAttorneyChange: (e, option) => onCompanyChange(e, option, COMPANY_CONFIGS[2]),
      onBuyerAttorneyChange: (e, option) => onCompanyChange(e, option, COMPANY_CONFIGS[3]),
    }),
    [onCompanyChange],
  );

  const onChangeTitleLocation = useCallback(() => {
    const companyId = formDataRef.current.titleAttendantCompanyId;
    const id = _.isObject(companyId) ? companyId?.value : companyId;

    if (id) {
      onOpenLocationTable({ value: id }, COMPANY_CONFIGS[0]);
    }
  }, [onOpenLocationTable, formDataRef]);

  const onChangeEscrowLocation = useCallback(() => {
    const companyId = formDataRef.current.escrowAttendantCompanyId;
    const id = _.isObject(companyId) ? companyId?.value : companyId;

    if (id) {
      onOpenLocationTable({ value: id }, COMPANY_CONFIGS[1]);
    }
  }, [onOpenLocationTable, formDataRef]);

  return {
    hasTitleCompany,
    setHasTitleCompany,
    hasEscrowCompany,
    setHasEscrowCompany,
    hasSellerAttorney,
    setHasSellerAttorney,
    hasBuyerAttorney,
    setHasBuyerAttorney,
    titleCompanyOptions,
    escrowCompanyOptions,
    sellerAttorneyOptions,
    buyerAttorneyOptions,
    onTitleCompanyChange,
    onEscrowCompanyChange,
    onSellerAttorneyChange,
    onBuyerAttorneyChange,
    onChangeTitleLocation,
    onChangeEscrowLocation,
  };
};
